// src/stores/useHealthUnitStore.ts
import { defineStore } from 'pinia'
import { router } from '@/router'
import { useRoute, LocationQuery } from 'vue-router'
import Network from '@/Network'
import { FiltersHealthUnits } from '@/Network/Types/Requests/HealthUnitRequests'
import { ListHealthUnitsResponse } from '@/Network/Types/Responses/HealthUnitResponses'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { getApiErrorMessage } from '@/utils/apiError'
import { useErrorToast } from '@/composables/useErrorToast'
import { SharedLoader, SharedLoadOptions, sharedLoaderFor } from '@/utils/sharedLoad'

export const HEALTH_UNITS_LOAD_ERROR = 'Não foi possível carregar as unidades'
export const HEALTH_UNITS_OPTIONS_ERROR = 'Erro ao buscar unidades, fale com um administrador.'

interface HealthUnitFilters {
  search: string
  ufs: string[]
  companies: string[]
}

interface ListHealthUnits {
  title: string
  value: string
  subtitle: string
  companyId?: string
}

export type HealthUnitSelectOption = {
  title: string
  value: number
  subtitle?: string
  /**
   * Empresa da unidade, para a cascata empresa -> unidade nos filtros.
   * Opcional de propósito: o campo é aditivo no backend, então o front tolera
   * uma resposta antiga (sem ele) sem quebrar tipagem nem esvaziar o select.
   */
  companyId?: number
  operationalGroup?: {
    id: number
    name: string
  } | null
}

export type HealthUnitState = {
  healthUnitsAdminAccess: HealthUnitSelectOption[]
  isLoadingSelectFetch: boolean
  healthUnits: ListHealthUnitsResponse | undefined
  listHealthUnits: ListHealthUnits[]
  listHealthUnitsByCompany: ListHealthUnits[]
  healthUnitsMap: Map<number, string>
  isLoading: boolean
  isError: boolean
  errorMessage: string | null
  /** Erro da última carga de `fetchAllHealthUnitsForSelect` — null quando ok. */
  selectLoadError: string | null
  healthUnitQuery: FiltersHealthUnits
  totalHealthUnits: number
  page: number
}

const DEFAULT_HEALTH_UNIT_LIMIT = 15

/** Cache + dedupe de GET /healthUnits/list-view, um por instância da store (ver `sharedLoad.ts`). */
const selectLoaders = new WeakMap<object, SharedLoader<HealthUnitSelectOption[]>>()
let _huFetchId = 0

export const useHealthUnitStore = defineStore('healthUnit', {
  state: (): HealthUnitState => ({
    healthUnitsAdminAccess: [],
    isLoadingSelectFetch: false,
    healthUnits: undefined,
    listHealthUnits: [],
    listHealthUnitsByCompany: [],
    healthUnitsMap: new Map(),
    isLoading: false,
    isError: false,
    errorMessage: null,
    selectLoadError: null,
    healthUnitQuery: {
      search: '',
      ufs: '',
      companies: '',
      limit: DEFAULT_HEALTH_UNIT_LIMIT,
      offset: 0
    },
    totalHealthUnits: 0,
    page: 1
  }),
  getters: {
    isFilterApplied(state): boolean {
      return (
        (state.healthUnitQuery.search?.trim().length ?? 0) > 0 ||
        (state.healthUnitQuery.ufs?.trim().length ?? 0) > 0 ||
        (state.healthUnitQuery.companies?.trim().length ?? 0) > 0
      )
    }
  },
  actions: {
    async getHealthUnitOptions(adminView: boolean = false): Promise<HealthUnitSelectOption[]> {
      try {
        this.isLoadingSelectFetch = true
        if (adminView) {
          const response = await Network.healthUnit.listAdminAccess()
          this.healthUnitsAdminAccess = response
          return response
        }
        const response = await Network.healthUnit.listAdminAccess()
        return response
      } catch (error) {
        // Os callers (CreateSchedule, Schedule/index, ScheduleBasicInfo) contam com o
        // `[]` para decidir (ex.: retry do import); a falha aparece aqui, no toast.
        useErrorToast().notifyError(error, HEALTH_UNITS_OPTIONS_ERROR)
        return []
      } finally {
        this.isLoadingSelectFetch = false
      }
    },
    // eslint-disable-next-line complexity
    deserializeFilters(query: LocationQuery): HealthUnitFilters {
      return {
        search: Array.isArray(query.search) ? String(query.search[0]) : (query.search ?? ''),
        ufs: Array.isArray(query.ufs)
          ? query.ufs
              .filter((u): u is string => typeof u === 'string')
              .join(',')
              .split(',')
          : typeof query.ufs === 'string'
            ? query.ufs.split(',')
            : [],
        companies: Array.isArray(query.companies)
          ? query.companies
              .filter((c): c is string => typeof c === 'string')
              .join(',')
              .split(',')
          : typeof query.companies === 'string'
            ? query.companies.split(',')
            : []
      }
    },
    serializeFilters(): Record<string, string> {
      const params = new URLSearchParams()

      if (this.healthUnitQuery.search) {
        params.set('search', this.healthUnitQuery.search)
      }
      if (this.healthUnitQuery.ufs) {
        params.set('ufs', this.healthUnitQuery.ufs)
      }
      if (this.healthUnitQuery.companies) {
        params.set('companies', this.healthUnitQuery.companies)
      }

      if (this.healthUnitQuery.limit) {
        params.set('limit', this.healthUnitQuery.limit.toString())
      }
      if (this.healthUnitQuery.offset !== undefined) {
        params.set('offset', this.healthUnitQuery.offset.toString())
      }

      const queryObject: Record<string, string> = {}
      params.forEach((value, key) => {
        queryObject[key] = value
      })

      return queryObject
    },
    updateQueryString() {
      const queryString = this.serializeFilters()
      router.replace({ query: queryString })
    },

    async setFiltersFromQuery(): Promise<FiltersHealthUnits> {
      const route = useRoute()
      const query = route.query

      const filters: HealthUnitFilters = this.deserializeFilters(query)

      this.healthUnitQuery.search = filters.search
      this.healthUnitQuery.ufs = filters.ufs.length > 0 ? filters.ufs.join(',') : ''
      this.healthUnitQuery.companies =
        filters.companies.length > 0 ? filters.companies.join(',') : ''

      this.healthUnitQuery.limit = query.limit ? Number(query.limit) : DEFAULT_HEALTH_UNIT_LIMIT
      this.healthUnitQuery.offset = query.offset ? Number(query.offset) : 0
      const { flags } = useFeatureFlags()
      if (!flags.enabledPermissions) await this.fetchHealthUnits()
      return this.healthUnitQuery
    },
    async fetchHealthUnits(): Promise<void> {
      const fetchId = ++_huFetchId
      try {
        this.isError = false
        this.errorMessage = null
        this.isLoading = true
        const response = await Network.healthUnit.list(this.healthUnitQuery)
        if (fetchId !== _huFetchId) return
        this.healthUnits = response
        this.listHealthUnits = (response?.data ?? []).map((h) => {
          const unit = {
            title: h.name,
            value: String(h.id),
            subtitle: h.company.name,
            companyId: String(h.company.id)
          }
          this.healthUnitsMap.set(+unit.value, unit.title)
          return unit
        })
        this.totalHealthUnits = response.count
      } catch (error) {
        if (fetchId !== _huFetchId) return
        console.error('Erro ao obter unidades de saúde:', error)
        this.isError = true
        this.errorMessage = getApiErrorMessage(error, HEALTH_UNITS_LOAD_ERROR)
        // Nenhum consumidor renderiza isError/errorMessage (o filtro da biometria facial
        // só lê healthUnits): sem o toast, a lista de unidades ficaria vazia em silêncio.
        useErrorToast().notifyError(error, HEALTH_UNITS_LOAD_ERROR)
      } finally {
        if (fetchId === _huFetchId) {
          this.isLoading = false
        }
      }
    },
    async applyFilters(
      newFilters: Partial<HealthUnitFilters>,
      isUpdateUrlQueryString: boolean = true
    ): Promise<FiltersHealthUnits> {
      if (newFilters.search !== undefined) {
        this.healthUnitQuery.search = newFilters.search
      }
      if (newFilters.ufs !== undefined) {
        this.healthUnitQuery.ufs = newFilters.ufs.join(',')
      }
      if (newFilters.companies !== undefined) {
        this.healthUnitQuery.companies = newFilters.companies.join(',')
      }

      this.healthUnitQuery.offset = 0
      if (isUpdateUrlQueryString) {
        this.updateQueryString()
      }

      const { flags } = useFeatureFlags()
      if (!flags.enabledPermissions) this.fetchHealthUnits()
      return this.healthUnitQuery
    },
    async clearFilters(): Promise<FiltersHealthUnits> {
      this.healthUnitQuery = {
        search: '',
        ufs: '',
        companies: '',
        limit: DEFAULT_HEALTH_UNIT_LIMIT,
        offset: 0
      }
      this.updateQueryString()

      const { flags } = useFeatureFlags()
      if (!flags.enabledPermissions) this.fetchHealthUnits()

      return this.healthUnitQuery
    },
    async changePage(page: number): Promise<FiltersHealthUnits> {
      this.page = page
      this.healthUnitQuery.offset = (page - 1) * this.healthUnitQuery.limit!
      this.updateQueryString()
      const { flags } = useFeatureFlags()
      if (!flags.enabledPermissions) await this.fetchHealthUnits()

      return this.healthUnitQuery
    },
    calculateOffSet(): number {
      return (this.page - 1) * this.healthUnitQuery.limit!
    },

    getPageCount(): number {
      return Math.ceil(this.totalHealthUnits / this.healthUnitQuery.limit!)
    },
    /**
     * Carga canônica dos selects de unidade: endpoint NÃO paginado, então a
     * lista nunca vem truncada. Substituiu o getAllHealthUnits, que atribuía o
     * healthUnitQuery inteiro e por isso vazava o filtro de uma tela para as
     * outras — o `healthUnitQuery` agora só é escrito pela própria store.
     *
     * Chamadas simultâneas compartilham a request e o resultado fica em cache por
     * 5 min no escopo (token + company_ids + época de escrita); `force` ignora o
     * cache. Cache quente ainda reconstrói `listHealthUnits`, que a listagem
     * paginada (`fetchHealthUnits`) também escreve.
     */
    async fetchAllHealthUnitsForSelect({ force = false }: SharedLoadOptions = {}): Promise<
      ListHealthUnits[]
    > {
      try {
        this.isLoading = true
        this.selectLoadError = null
        const response = await sharedLoaderFor(selectLoaders, this).load(
          () => Network.healthUnit.listViewAccess(),
          { force }
        )
        this.listHealthUnits = response.map((h) => {
          const unit = {
            title: h.title,
            value: String(h.value),
            subtitle: h.subtitle ?? '',
            // String() obrigatório: os companyIds dos filtros vêm de
            // useCompanyStore como string, e fetchHealthUnits já coage igual.
            companyId: h.companyId !== undefined ? String(h.companyId) : undefined
          }
          this.healthUnitsMap.set(+unit.value, unit.title)
          return unit
        })
        return this.listHealthUnits
      } catch (error) {
        // fallback intencional: a carga é compartilhada por ~13 selects (várias chamadas
        // simultâneas por tela); toast aqui duplicaria a notificação. O estado fica em
        // `selectLoadError`, que o filtro mobile do dashboard exibe no campo
        // (useFilterOptions → MobileFiltersSheet `:error`).
        console.error('Erro ao obter unidades de saúde:', error)
        this.selectLoadError = getApiErrorMessage(error, HEALTH_UNITS_LOAD_ERROR)
        return []
      } finally {
        this.isLoading = false
      }
    },

    getHealthUnitNameById(id: number): ListHealthUnits | undefined {
      return this.listHealthUnits.find((healthUnit) => healthUnit.value === String(id))
    },
    async setHealthUnitsByCompany(companyId: number | null) {
      if (!companyId) {
        this.listHealthUnitsByCompany = []
        return
      }
      // `list` propaga o erro desde que parou de devolver lista vazia em falha;
      // aqui ele para no store, com toast (mensagem do backend via notifyError), em vez
      // de estourar nos callers (a maioria chama sem catch).
      try {
        const response = await Network.healthUnit.list({
          limit: 1000,
          companies: String(companyId)
        })
        this.listHealthUnitsByCompany = (response?.data ?? []).map((h) => ({
          title: h.name,
          value: String(h.id),
          subtitle: h.company.name
        }))
      } catch (error) {
        console.error('Erro ao obter unidades de saúde da empresa:', error)
        this.listHealthUnitsByCompany = []
        useErrorToast().notifyError(error, HEALTH_UNITS_OPTIONS_ERROR)
      }
    }
  }
})
